import React, { FC } from 'react';
import { Box, Flex, FlexProps, Link, LinkProps, Text } from '@chakra-ui/react';
import { useTranslate } from 'ts/common/hooks';

// The client portfolio app's bootstrap data; absent elsewhere (gallery, Studio).
type WindowWithClientBrandBootstrap = Window & {
    SpClientBrandBootstrap?: { footerCredit?: { href: string | null; show: boolean } };
};

/**
 * URL of the "Created by ShootProof" credit, or null when the page shouldn't show it. The server
 * decides per page; anything missing or malformed hides the credit.
 */
const getFooterCreditHref = (): string | null => {
    const footerCredit = (window as WindowWithClientBrandBootstrap).SpClientBrandBootstrap
        ?.footerCredit;

    if (!footerCredit || footerCredit.show !== true) {
        return null;
    }

    if (typeof footerCredit.href !== 'string' || footerCredit.href === '') {
        return null;
    }

    return footerCredit.href;
};

interface Props extends FlexProps {
    /** True if the user is in Compare mode */
    isComparing?: boolean;
    /** True if the Package Builder is open */
    isPackageBuilderOpen?: boolean;
    /** True if the component is being rendered in Portal */
    isPortal?: boolean;
}

const linkProps: LinkProps = {
    target: '_blank',
    fontSize: 'xs',
    color: 'currentColor',
    _focus: {
        boxShadow: 'none'
    },
    _focusVisible: {
        boxShadow: 'outline'
    },
    _visited: {
        color: 'currentColor'
    }
};

const TermsAndPolicyLinks: FC<Props> = ({
    isComparing,
    isPackageBuilderOpen,
    isPortal,
    ...props
}) => {
    const t = useTranslate();

    if (isComparing || isPackageBuilderOpen) {
        return null;
    }

    const footerCreditHref = getFooterCreditHref();

    return (
        <Flex
            height="100%"
            width="100%"
            direction="column"
            alignItems="center"
            justifyContent={isPortal ? 'center' : 'flex-end'}
            gap="4px"
            marginBottom={{ base: '0', lg: '0' }}
            paddingTop="15px"
            paddingBottom={isPortal ? { base: '15px', lg: '15px' } : { base: '125px', lg: '15px' }}
            className="terms-and-privacy-links"
            data-testid="terms-and-privacy-links"
            {...props}
        >
            <Flex>
                <Link
                    href="https://www.shootproof.com/legal/photographer-website-terms-of-use/"
                    rel="nofollow noopener"
                    {...linkProps}
                >
                    <Text>{t('termsOfUse')}</Text>
                </Link>
                <Box width="20px" textAlign="center">
                    <Text fontSize="xs">|</Text>
                </Box>
                <Link
                    href="https://www.shootproof.com/legal/privacy-policy/"
                    rel="nofollow noopener"
                    {...linkProps}
                >
                    <Text>{t('privacyPolicy')}</Text>
                </Link>
            </Flex>
            {footerCreditHref && (
                // No nofollow: the credit is meant to be a followed link.
                <Link
                    href={footerCreditHref}
                    rel="noopener"
                    data-testid="footer-credit"
                    {...linkProps}
                >
                    <Text>{t('createdByShootProof')}</Text>
                </Link>
            )}
        </Flex>
    );
};

export default TermsAndPolicyLinks;
